import htmlReactParser from 'html-react-parser';
import { format, parse } from 'date-fns';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';
import React from 'react';
import JsonQuery from 'json-query';
import Handlebars from 'handlebars';
import frResources from './resources/fr-CA.json';
import enResources from './resources/en-US.json';
import germanResources from './resources/de-DE.json';
import { baseFormats, frenchFormats } from './formats';
import supportedLocales from './supportedLocales';

export interface ILocalizationState {
    locale: string;
    currency: string;
    resources: {
        [key: string]: string;
    };
}

export interface ILocaleObject {}
export type Replacements = Record<string, string | number>;
export type ReplacementsGetter = (key: string, replacements: Replacements) => string;

export interface ILocalizationSettings {
    availableLocales: Record<string, string>;
    localeKey: string;
    translationsObject: Record<string, any>;
    getTranslations: (() => Record<string, any>) | null;
    getLocale: (() => string) | null;
    handleMissingTranslation: (
        key: string,
        replacements: Record<string, string | number>,
        options: any,
        err: any
    ) => string | undefined;
    handleFailedLocalization: (value: any, options: any, err: any) => string | undefined;
    localeObject: Locale | null;

    get translations(): Record<string, any>;
    set translations(translations);

    get locale();
    set locale(locale: string);
}

export class LocalizationSettings implements ILocalizationSettings {
    availableLocales: Record<string, string> = supportedLocales;
    localeKey: string = 'en';
    translationsObject: Record<string, any> = {};
    getTranslations: (() => Record<string, any>) | null = null;
    getLocale: (() => string) | null = null;
    handleMissingTranslation = (text: string) => text.split('.').pop();
    handleFailedLocalization = () => undefined;
    localeObject: Locale | null = null;

    get translations() {
        return this.getTranslations ? this.getTranslations() : this.translationsObject;
    }

    set translations(translations) {
        this.translationsObject = translations;
    }

    get locale() {
        return this.getLocale ? this.getLocale() : this.localeKey;
    }

    set locale(locale) {
        this.localeKey = locale;
    }
}

export interface II18N {
    translate: (
        key: string,
        replacements?: Replacements,
        options?: { locale?: string; returnNullOnError?: boolean; returnKeyOnError?: boolean }
    ) => string | Record<string, any> | null | undefined;

    localize: (
        value: any,
        options:
            | { locale?: string; dateFormat?: string; parseFormat?: string }
            | Intl.NumberFormatOptions
    ) => any;

    t(value: string | { key: string; values: object }, key?: string, nullguard?: string): string;

    getLocale(): string;

    format(template: any, ctx?: any): string;

    // todo: see if we can remove this,
    // added only because it was passed to transformDataWithGlobals in a couple of places
    getHandlebars(): typeof Handlebars;
}

export class I18N implements II18N {
    settings: ILocalizationSettings;
    handlebars: typeof Handlebars;
    annotator: any;

    constructor(settings: ILocalizationSettings, annotator: any) {
        this.settings = settings;

        this.handlebars = Handlebars.create();

        this.annotator = annotator;

        this.handlebars.registerHelper('format', (per, format, replace, replaceWith, nullGuard) => {
            try {
                return per
                    ? this.t(per, format)?.toString().replace(replace, replaceWith)
                    : nullGuard;
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper(
            'formatIncludingZero',
            (per, format, replace, replaceWith, nullGuard) => {
                try {
                    return per || per === 0
                        ? this.t(per, format)?.toString().replace(replace, replaceWith)
                        : nullGuard;
                } catch (e) {
                    console.log(`Handlebars Error ${e}`);
                }
            }
        );

        this.handlebars.registerHelper('replace', (per, replace, replaceWith, nullGuard) => {
            try {
                return per ? per.toString().replace(replace, replaceWith) : nullGuard;
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('query', (per, query) => {
            try {
                const value = per && JsonQuery(`${query}`, { data: per })?.value;
                return value || '';
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('isDefined', (per, nullGuard) => {
            try {
                return per || nullGuard;
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper(
            'formatUniversalDate',
            (per, format, replace, replaceWith, nullGuard) => {
                try {
                    if (per) {
                        if (typeof per === 'string') {
                            per = per.replace(/-/g, '/');
                        }

                        return this.t(per, format)?.toString().replace(replace, replaceWith);
                    } else {
                        return nullGuard;
                    }
                } catch (e) {
                    console.log(`Handlebars Error ${e}`);
                }
            }
        );

        this.handlebars.registerHelper('ifEqual', function (this: any, a, b, opts) {
            try {
                if (a === b) {
                    return opts.fn(this);
                } else {
                    return opts.inverse(this);
                }
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('includes', function (this: any, str, subStr, opts) {
            try {
                if (str?.includes(subStr)) {
                    return opts.fn(this);
                } else {
                    return opts.inverse(this);
                }
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('capitalize', function (aString, nullGuard) {
            try {
                return aString ? aString.toUpperCase() : nullGuard;
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('lowercase', function (aString, nullGuard) {
            try {
                return aString ? aString.toLowerCase() : nullGuard;
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('byId', (id: string) => {
            try {
                return this.annotator?.markersById(id);
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });

        this.handlebars.registerHelper('byMarkerTargetId', (id: string) => {
            try {
                return this.annotator?.markersByMarkerTargetId(id);
            } catch (e) {
                console.log(`Handlebars Error ${e}`);
            }
        });
    }

    getLocale() {
        return this.settings.locale;
    }

    private fetchTranslation(
        translations: Record<string, any>,
        key: string,
        count: number | null = null
    ): string {
        const _index = key.indexOf('.');

        if (typeof translations === 'undefined') {
            throw new Error('not found');
        }

        if (_index > -1) {
            return this.fetchTranslation(
                translations[key.substring(0, _index)],
                key.substr(_index + 1),
                count
            );
        }

        if (count !== null) {
            if (translations[`${key}_${count}`]) {
                // when key = 'items_3' if count is 3
                return translations[`${key}_${count}`];
            }

            if (count !== 1 && translations[`${key}_plural`]) {
                // when count is not simply singular, return _plural
                return translations[`${key}_plural`];
            }
        }

        if (translations[key] != null) {
            return translations[key];
        }

        throw new Error('not found');
    }

    private replace(translation: string | Record<string, any>, replacements: Replacements) {
        if (typeof translation === 'string') {
            let result = translation;
            Object.keys(replacements).forEach((replacement) => {
                result = result
                    .split(`%{${replacement}}`)
                    .join(replacements[replacement]?.toString() ?? '');
            });
            return result;
        }

        if (React.isValidElement(translation)) {
            return translation;
        }

        if (typeof translation === 'object') {
            const result: Record<string, any> = {};
            Object.keys(translation).forEach((translationKey) => {
                result[translationKey] = this.replace(
                    translation[translationKey],
                    replacements
                )?.toString();
            });
            return result;
        }

        return null;
    }

    translate = (
        key: string,
        replacements: { count?: number } = {},
        options: { locale?: string; returnNullOnError?: boolean; returnKeyOnError?: boolean } = {}
    ) => {
        const locale = options.locale || this.settings.locale;
        let translation = '';

        try {
            const translationLocale = this.settings.translations[locale]
                ? locale
                : locale.split('-')[0];
            translation = this.fetchTranslation(
                this.settings.translations,
                `${translationLocale}.${key}`,
                replacements.count
            );
        } catch (err) {
            if (options.returnNullOnError) return null;

            if (options.returnKeyOnError) return key;

            return this.settings.handleMissingTranslation(key, replacements, options, err);
        }

        return this.replace(translation, replacements);
    };

    localize = (
        value: any,
        options:
            | { locale?: string; dateFormat?: string; parseFormat?: string }
            | Intl.NumberFormatOptions = {}
    ) => {
        const dateOptions = options as {
            locale?: string;
            dateFormat?: string;
            parseFormat?: string;
        };
        const locale = dateOptions.locale || this.settings.locale;
        
        if (dateOptions.dateFormat) {
            try {
                const localeObject = this.settings.localeObject;

                if (!localeObject) throw new Error(`Locale ${locale} not added`);

                let parsedDate: Date | null = dateOptions.parseFormat
                    ? parse(
                          value,

                          // todo: differentiate between the two translate functions
                          this.translate(
                              dateOptions.parseFormat,
                              {},
                              { locale, returnKeyOnError: true }
                          ) as string,
                          new Date(),
                          { locale: localeObject }
                      )
                    : new Date(value);
                    
                if (isNaN(parsedDate as unknown as number)) {
                    return null;
                } else {
                    parsedDate = new Date(
                        parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60 * 1000
                    );
                }

                if (dateOptions.dateFormat === 'distance-to-now') {
                    return formatDistanceToNowStrict(parsedDate, {
                        addSuffix: true,
                        locale: localeObject,
                    });
                }

                if (dateOptions.dateFormat === 'distance-to-now-hours') {
                    return formatDistanceToNowStrict(parsedDate, {
                        addSuffix: true,
                        unit: 'hour',
                        locale: localeObject,
                    });
                }

                if (dateOptions.dateFormat === 'distance-to-now-days') {
                    return formatDistanceToNowStrict(parsedDate, {
                        addSuffix: true,
                        unit: 'day',
                        locale: localeObject,
                    });
                }

                return format(
                    parsedDate,
                    this.translate(
                        dateOptions.dateFormat,
                        {},
                        { locale, returnKeyOnError: true }
                    ) as string,
                    { locale: localeObject }
                );
            } catch (err) {
                return this.settings.handleFailedLocalization(value, dateOptions, err);
            }
        }

        if (typeof value === 'number') {
            const numberFormatOptions = options as Intl.NumberFormatOptions;

            try {
                return new Intl.NumberFormat(locale, numberFormatOptions).format(value);
            } catch (err) {
                return this.settings.handleFailedLocalization(value, options, err);
            }
        }

        return value;
    };

    t(value: string | { key: string; values: object }, key?: string, nullguard?: string) {
        if (!key) {
            if (typeof value === 'object') {
                // This method is used to insert values into the string template
                return this.translate(value.key, value.values as Replacements);
            }

            const resourceString = this.translate(value);

            // if the resource string contains any html,
            // convert it to a React element
            if (typeof resourceString === 'string' && /<\/?[a-z][\s\S]*>/i.test(resourceString)) {
                return htmlReactParser(resourceString);
            }

            // otherwise just return the resource string
            return resourceString;
        }

        const locale = this.getLocale();
        const allTanslations = this.settings.translations;

        if (!locale || !allTanslations) {
            return value;
        }

        const translations = allTanslations[locale];

        if (nullguard && !value) {
            return translations[`nullguards_${nullguard}`];
        }

        // object from format list
        const options = translations[key];

        return this.localize(value as string | number, options);
    }

    format(template: any, ctx: any = {}) {
        // ctx is immutable, do not assign locale to it.
        const ctxWithLocale = {
            ...ctx,
            locale:
                typeof ctx.locale === 'string' && ctx.locale.length > 0
                    ? ctx.locale
                    : this.settings.locale,
        };

        try {
            return this.handlebars.compile(template || '')(ctxWithLocale);
        } catch (e) {
            console.log(`Handlebars Error ${e} `);
            return '';
        }
    }

    getHandlebars() {
        return this.handlebars;
    }
}

export function getLocalization(
    locale?: string,
    currency?: string,
    resources?: {
        [key: string]: any;
    },
    annotator?: any,
    localeObject?: Locale
) {
    locale = locale || 'en-US';
    currency = currency || 'CAD';
    resources = resources || {};

    const settings = new LocalizationSettings();
    settings.availableLocales = supportedLocales;
    settings.locale = locale;
    settings.localeObject = localeObject || null;

    const supportedLocalesKeys = Object.keys(supportedLocales);

    // default translations
    const translations: { [locale: string]: { [key: string]: any } } = {
        'en-US': {
            ...enResources,
            ...baseFormats,
        },
        'fr-CA': {
            ...frResources,
            ...frenchFormats,
        },
        'de-DE': {
            ...germanResources,
            ...baseFormats,
        },
    };

    supportedLocalesKeys.forEach((key) => {
        if (!translations[key]) {
            translations[key] = { ...baseFormats };
        }
    });

    // merge default translations with user provided resourcess for current locale
    settings.translations = {
        ...translations,
        [locale]: {
            ...translations[locale],
            ...resources,
        },
    };

    const loc = new I18N(settings, annotator) as II18N;
    return loc;
}
